"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Modal } from "@/components/ds/modal";
import type { DashboardRangeKey, DashboardTimeRangeSelection } from "@/components/dashboard/widget-config";
import { ThemedSelect } from "@/components/ui-system/select";
import { useWidgetAggregateSeries } from "@/hooks/useWidgetAggregateSeries";
import { buildCreateAlertFromWidgetHref } from "@/modules/alerts/utils/create-alert-from-widget";
import { useDeviceChannelsQuery } from "@/modules/devices/hooks";
import { TelemetryUPlotChart } from "@/modules/telemetry/components/telemetry-uplot-chart";
import { getChannelDisplayLabel } from "@/modules/telemetry/page-utils";

import { WidgetChrome } from "./WidgetChrome";
import type { WidgetShellProps } from "./types";

const RANGE_OPTIONS: { value: DashboardRangeKey; label: string; description: string }[] = [
  { value: "last_1h", label: "1 hr", description: "Last one hour" },
  { value: "last_24h", label: "1 day", description: "Last 24 hours" },
  { value: "last_7d", label: "7 days", description: "Last seven days" },
  { value: "custom", label: "Custom", description: "Pick a start and end time" },
];

type DashboardCustomRangeSelection = Extract<DashboardTimeRangeSelection, { key: "custom" }>;

function formatDateTimeLocalInput(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

function initialCustomRange(): DashboardCustomRangeSelection {
  const end = new Date();
  const start = new Date(end.getTime() - 86_400_000);
  return {
    key: "custom",
    start: formatDateTimeLocalInput(start),
    end: formatDateTimeLocalInput(end),
  };
}

function selectableRangeKey(key: DashboardTimeRangeSelection["key"]): DashboardTimeRangeSelection {
  return key === "custom" ? initialCustomRange() : { key: key === "last_5m" ? "last_1h" : key };
}

function isValidCustomRange(range: DashboardTimeRangeSelection): boolean {
  if (range.key !== "custom" || !range.start || !range.end) {
    return false;
  }
  const start = new Date(range.start).getTime();
  const end = new Date(range.end).getTime();
  return Number.isFinite(start) && Number.isFinite(end) && end > start;
}

export function LineChartWidget({ widget, config, timeRange, customRange, hasActiveAlert }: WidgetShellProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 400, h: 200 });
  const [range, setRange] = useState<DashboardTimeRangeSelection>(() => customRange ?? selectableRangeKey(timeRange));
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [draftCustomRange, setDraftCustomRange] = useState<DashboardCustomRangeSelection>(() =>
    customRange?.key === "custom" ? customRange : initialCustomRange(),
  );

  useEffect(() => {
    setRange(customRange ?? selectableRangeKey(timeRange));
  }, [customRange, timeRange]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(() => {
      const w = Math.max(240, Math.floor(el.clientWidth));
      setDims({ w, h: 220 });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const seriesKey = config.series_key;
  const channelsQuery = useDeviceChannelsQuery(config.device_id, Boolean(config.channel));
  const channelLabel = useMemo(() => {
    const channel = channelsQuery.data?.find((item) => item.key === config.channel);
    return channel ? getChannelDisplayLabel(channel) : config.channel;
  }, [channelsQuery.data, config.channel]);
  const effectiveTimeRange = range.key === "custom" ? timeRange : range.key;
  const effectiveCustomRange = range.key === "custom" ? range : null;
  const usesLocalOverride =
    range.key === "custom" ||
    Boolean(customRange) ||
    range.key !== timeRange;

  const { data, isLoading, error, dataUpdatedAt } = useWidgetAggregateSeries(widget.id, widget.type, {
    deviceId: config.device_id,
    channel: seriesKey,
    timeRange: effectiveTimeRange,
    customRange: effectiveCustomRange,
    aggregation: config.aggregation,
    refreshIntervalSeconds: config.refresh_interval_seconds,
    preferSingleFetch: usesLocalOverride,
  });

  const points = data?.points ?? [];
  const configuredTitle = config.title?.trim();
  const title = 
    (configuredTitle && configuredTitle !== config.channel ? configuredTitle : "") ||
    config.formula?.trim() ||
    config.metric_id?.slice(0, 8) ||
    channelLabel ||
    "Series";
  const revision = dataUpdatedAt;
  const seriesLabels = useMemo(() => ({ [seriesKey]: title }), [seriesKey, title]);

  return (
    <>
      <WidgetChrome
        widget={widget}
        title={title}
        subtitle="Line chart - bucketed aggregates"
        isLoading={isLoading && points.length === 0}
        error={error}
        createAlertHref={buildCreateAlertFromWidgetHref(config)}
        hasActiveAlert={hasActiveAlert}
        headerActions={
          <ThemedSelect
            value={range.key}
            ariaLabel={`${title} range`}
            className="w-[7.5rem]"
            triggerClassName="min-h-8 rounded-lg border-border bg-card px-2.5 py-1 text-[12px] shadow-sm"
            menuClassName="min-w-[13rem] rounded-xl border-border bg-card p-ds-2 shadow-lg"
            options={RANGE_OPTIONS}
            onChange={(value) => {
              const next = value as DashboardRangeKey;
              if (next === "custom") {
                const draft = range.key === "custom" ? range : customRange?.key === "custom" ? customRange : initialCustomRange();
                setDraftCustomRange(draft);
                setCustomModalOpen(true);
                return;
              }
              setRange({ key: next });
            }}
          />
        }
      >
        {!error && points.length === 0 && !isLoading ? (
          <p className="text-caption text-muted">No points in this window.</p>
        ) : !error && points.length > 0 ? (
          <div ref={wrapRef} className="min-h-[14rem] w-full">
            <TelemetryUPlotChart
              width={dims.w}
              height={dims.h}
              deviceId={config.device_id}
              metricKeys={[seriesKey]}
              aggregatePoints={points}
              dataRevision={revision}
              liveEnabled={false}
              liveWindowPoints={512}
              seriesLabels={seriesLabels}
            />
          </div>
        ) : !error && isLoading ? null : null}
      </WidgetChrome>

      <Modal
        open={customModalOpen}
        onClose={() => setCustomModalOpen(false)}
        title="Custom chart range"
        description="Choose the time window used for this graph."
        footer={
          <div className="flex justify-end gap-ds-2">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCustomModalOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setRange(draftCustomRange);
                setCustomModalOpen(false);
              }}
              disabled={!isValidCustomRange(draftCustomRange)}
            >
              Apply range
            </button>
          </div>
        }
      >
        <div className="grid gap-ds-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Start</span>
            <input
              type="datetime-local"
              value={draftCustomRange.start}
              onChange={(event) => setDraftCustomRange((current) => ({ ...current, start: event.target.value }))}
              className="h-11 rounded-lg border border-border bg-card px-4 text-caption text-foreground shadow-sm outline-none transition-colors focus:border-primary"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">End</span>
            <input
              type="datetime-local"
              value={draftCustomRange.end}
              onChange={(event) => setDraftCustomRange((current) => ({ ...current, end: event.target.value }))}
              className="h-11 rounded-lg border border-border bg-card px-4 text-caption text-foreground shadow-sm outline-none transition-colors focus:border-primary"
            />
          </label>
        </div>
      </Modal>
    </>
  );
}
